import { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faMagicWandSparkles } from "@fortawesome/free-solid-svg-icons";
import { Col } from "react-bootstrap";
import CrudApi from "@serie3/common/api/crudApi";
import DocumentImage from "../../../../documents/DocumentImage";
import CustomModal from "../../../../../../layout/CustomModal";
import { useNavigate } from "react-router-dom";

const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const BillDropzone = ({ bill, uploadUrl, previewUrl, formik, setAnalyzing: setAnalyzingExternal }) => {
  const [file, setFile] = useState(null);
  const [originalName, setOriginalName] = useState(bill?.original_name || null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [analyzingLocal, setAnalyzingLocal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState(null);
  const [existingBill, setExistingBill] = useState(null);
  const [existingPreview, setExistingPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!existingBill) { setExistingPreview(null); return; }
    CrudApi.getBinary(`/finance/in/bills/filePreview/${existingBill.id}?t=${Date.now()}`)
      .then(res => setExistingPreview(res?.data ? { header: res.headers, data: res.data } : null))
      .catch(() => setExistingPreview(null));
  }, [existingBill]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const setAnalyzing = (val) => {
    setAnalyzingLocal(val);
    if (setAnalyzingExternal) setAnalyzingExternal(val);
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const response = await CrudApi.getBinary(`${previewUrl}?t=${Date.now()}`);
      setFile(response?.data ? { header: response.headers, data: response.data } : null);
    } catch {
      setFile(null);
    }
    setLoading(false);
  };

  useEffect(() => { loadPreview(); }, [reloadKey]);

  const analyzeAndFill = async () => {
    setAnalyzing(true);
    const analysis = await CrudApi.justGet("/finance/in/bills/analyzeFile/" + bill.id);
    let raw = analysis.data;
    if (raw && formik) {
      if (typeof raw === "string") {
        raw = raw.replace(/^```json\s*/i, "").replace(/```\s*$/, "").trim();
        try { raw = JSON.parse(raw); } catch { setAnalyzing(false); return; }
      }
      const merged = { ...formik.values, ...raw };
      if (!merged.betreff) {
        if (merged.beschreibung) {
          merged.betreff = merged.beschreibung;
        } else {
          let customerName = "";
          try {
            const addr = typeof bill.fixed_address === "string" ? JSON.parse(bill.fixed_address) : bill.fixed_address;
            customerName = addr?.firmenname || [addr?.vorname, addr?.name].filter(Boolean).join(" ");
          } catch {}
          const billNo = merged.rechnungs_nummer || formik.values.rechnungs_nummer || "";
          merged.betreff = [customerName, billNo].filter(Boolean).join(" – ");
        }
      }
      formik.setValues(merged);
    }
    setAnalyzing(false);
  };

  const doUpload = async (uploadData) => {
    const response = await CrudApi.edit(uploadUrl, uploadData);
    if (response?.status === 409) {
      setPendingUploadData(uploadData);
      setExistingBill(response.data?.existing ?? null);
      return false;
    }
    setReloadKey((k) => k + 1);
    analyzeAndFill();
    return true;
  };

  const forceUpload = async () => {
    if (!pendingUploadData) return;
    setPendingUploadData(null);
    setExistingBill(null);
    setExistingPreview(null);
    setUploading(true);
    await doUpload({ ...pendingUploadData, forced: true });
    setUploading(false);
  };

  const uploadFile = async (droppedFile) => {
    setOriginalName(droppedFile.name);
    setUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(droppedFile);
    reader.onloadend = async () => {
      const uploadData = {
        file_string: reader.result,
        file_name: droppedFile.name,
        original_name: droppedFile.name,
        id: bill.id,
        forced: false,
      };
      await doUpload(uploadData);
      setUploading(false);
    };
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) uploadFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 5000000,
    multiple: false,
  });

  const Spinner = () => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
      <div style={{
        width: "40px", height: "40px", border: "3px solid #e8f0fe",
        borderTop: "3px solid #1a73e8", borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const forceModal = (
    <CustomModal show={!!pendingUploadData} setShow={() => { setPendingUploadData(null); setExistingBill(null); }} title="File already exists">
      <div style={{ padding: "24px" }}>
        <div style={{ fontSize: "0.85rem", color: "#616161", marginBottom: "16px" }}>
          A file with this name is already attached to another bill.
        </div>

        {existingBill && (
          <div style={{ background: "#f8f9fb", borderRadius: "10px", padding: "14px 16px", marginBottom: "16px", border: "1px solid #e0e0e0" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#9e9e9e", marginBottom: "8px" }}>Existing bill</div>
            <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#212121", marginBottom: "2px" }}>
              {existingBill.original_name}
            </div>
            <div style={{ fontSize: "0.78rem", color: "#616161", marginBottom: "2px" }}>
              {existingBill.typ} · {existingBill.datum}
            </div>
            <div style={{ fontSize: "0.78rem", color: existingBill.draft ? "#f57c00" : "#2e7d32", marginBottom: "12px" }}>
              {existingBill.draft ? "Draft" : "Final"}
            </div>

            {existingPreview && (
              <div style={{ borderRadius: "8px", overflow: "hidden", marginBottom: "12px", maxHeight: "320px", border: "1px solid #e0e0e0" }}>
                <DocumentImage file={existingPreview} />
              </div>
            )}

            <button
              type="button"
              onClick={() => { setPendingUploadData(null); setExistingBill(null); setExistingPreview(null); navigate(`/finance/in/bills/edit/${existingBill.id}`); }}
              style={{ padding: "6px 14px", border: "none", borderRadius: "8px", background: "#e8f0fe", color: "#1a73e8", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}
            >
              Open bill
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            onClick={() => { setPendingUploadData(null); setExistingBill(null); }}
            style={{ padding: "9px 20px", border: "none", borderRadius: "10px", background: "#f5f5f5", color: "#616161", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={forceUpload}
            style={{ padding: "9px 20px", border: "none", borderRadius: "10px", background: "#e53935", color: "#fff", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}
          >
            Upload anyway
          </button>
        </div>
      </div>
    </CustomModal>
  );

  if (isMobile) {
    return (
      <div style={{ marginBottom: "16px" }}>
        <div
          {...getRootProps()}
          style={{
            padding: "16px", borderRadius: "10px", textAlign: "center",
            border: `2px dashed ${isDragActive ? "#1a73e8" : "#c5d9f8"}`,
            background: isDragActive ? "#eef4ff" : "#f5f8ff",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: "1.4rem", color: "#1a73e8", marginBottom: "6px" }} />
          <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#424242" }}>
            {uploading ? "Uploading…" : isDragActive ? "Drop here" : file ? "Drop to replace" : "Tap to upload bill document"}
          </div>
        </div>
        {file && (
          <button
            type="button"
            onClick={analyzeAndFill}
            disabled={analyzingLocal}
            style={{
              marginTop: "10px", width: "100%", padding: "12px 0",
              background: "#e8f0fe", color: "#1a73e8",
              border: "none", borderRadius: "10px", fontSize: "0.92rem", fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            <FontAwesomeIcon icon={faMagicWandSparkles} />
            {analyzingLocal ? "Analyzing…" : "Update from document"}
          </button>
        )}
        {forceModal}
      </div>
    );
  }

  return (
    <Col style={{ padding: "0 12px 0 0", background: "transparent" }}>
      <div
        {...getRootProps()}
        style={{
          position: "relative",
          minHeight: "520px",
          borderRadius: "14px",
          cursor: "pointer",
          overflow: "hidden",
          transition: "box-shadow 0.2s",
          ...(file && !isDragActive
            ? {
                background: "#f8f9fb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }
            : {
                border: `2px dashed ${isDragActive ? "#1a73e8" : "#c5d9f8"}`,
                background: isDragActive ? "#eef4ff" : "transparent",
                boxShadow: isDragActive ? "0 8px 32px rgba(26,115,232,0.2)" : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
              }),
        }}
      >
        <input {...getInputProps()} />

        {loading || uploading ? (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <Spinner />
          </div>
        ) : file && !isDragActive ? (
          <>
            <DocumentImage file={file} />
            <div style={{
              position: "absolute", inset: 0,
              background: "rgba(26,115,232,0.0)",
              transition: "background 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "14px",
            }} />
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "48px 32px", pointerEvents: "none" }}>
            <div style={{
              width: "72px", height: "72px", borderRadius: "50%",
              background: isDragActive ? "#1a73e8" : "#e8f0fe",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", transition: "background 0.2s",
            }}>
              <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: "1.8rem", color: isDragActive ? "#fff" : "#1a73e8" }} />
            </div>
            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: isDragActive ? "#1a73e8" : "#212121", marginBottom: "8px" }}>
              {isDragActive ? "Drop to upload" : "No file attached"}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#9e9e9e", lineHeight: 1.7, marginBottom: "24px" }}>
              Drag & drop a PDF or image here,<br />or <span style={{ color: "#1a73e8", fontWeight: 700 }}>click anywhere to browse</span>
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "11px 28px", borderRadius: "10px",
              background: isDragActive ? "#1a73e8" : "#1a73e8",
              color: "#fff", fontSize: "0.92rem", fontWeight: 700,
              boxShadow: "0 2px 10px rgba(26,115,232,0.35)",
            }}>
              <FontAwesomeIcon icon={faCloudArrowUp} /> Click here to upload
            </div>
          </div>
        )}

        {/* Drag-over overlay when file already exists */}
        {file && isDragActive && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(26,115,232,0.15)",
            border: "3px solid #1a73e8",
            borderRadius: "14px",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "rgba(255,255,255,0.95)", borderRadius: "10px",
              padding: "12px 22px", fontWeight: 700, color: "#1a73e8", fontSize: "0.95rem",
            }}>
              Drop to replace document
            </div>
          </div>
        )}
      </div>

      {originalName && (
        <div style={{
          marginTop: "8px", padding: "7px 12px",
          background: "#f8f9fb", borderRadius: "8px",
          fontSize: "0.8rem", color: "#616161",
          display: "flex", alignItems: "center", gap: "6px",
          overflow: "hidden",
        }}>
          <span style={{ fontWeight: 600, flexShrink: 0 }}>File:</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{originalName}</span>
        </div>
      )}

      {file && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); analyzeAndFill(); }}
          disabled={analyzingLocal}
          style={{
            marginTop: "10px", width: "100%", padding: "10px 0",
            background: "#e8f0fe", color: "#1a73e8",
            border: "none", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          {analyzingLocal
            ? <><span style={{ width: "14px", height: "14px", border: "2px solid #a8c4f5", borderTopColor: "#1a73e8", borderRadius: "50%", animation: "spin 0.75s linear infinite", display: "inline-block" }} /> Analyzing…</>
            : <><FontAwesomeIcon icon={faMagicWandSparkles} /> Update from document</>
          }
        </button>
      )}
      {forceModal}
    </Col>
  );
};

export default BillDropzone;
